import { makeStyles } from "@material-ui/core";
import React, { useContext, useEffect, useRef, useState } from "react";
import LazyLoad, { forceCheck } from "react-lazyload";

import {
  AlgoliaContext,
  SearchOptions as AlgoliaOptions,
} from "../../../../../contexts/algolia";
import { LanguageContext } from "../../../../../contexts/language";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "block",
    width: "100%",
    overflowX: "hidden",
    background: "#fa255e00",
    // margin: (props: any) =>
    //   `${props.mt}px ${props.mr}px ${props.mb}px ${props.ml}px`,
    [theme.breakpoints.down("xl")]: {
      marginBottom: theme.spacing(10),
    },
    [theme.breakpoints.down("lg")]: {
      marginBottom: theme.spacing(10),
    },
    [theme.breakpoints.down("md")]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(3),
    },
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(3),
    },
  },
}));

type FetchOptions = RequestInit & {
  url?: Request | string;
};
interface SourceConfig {
  fetch?: FetchOptions;
  algolia?: AlgoliaOptions;
}

interface Props {
  mt?: number;
  mr?: number;
  mb?: number;
  ml?: number;
  srcConfig?: SourceConfig;
  renderElement: (source?: any) => JSX.Element;
  renderPlaceholder?: () => JSX.Element;
  lazyLoad?: boolean;
}

const fetchServerDataSource = async (options: FetchOptions) => {
  const { url, ...rest } = options;
  const response = await fetch(url, { ...rest });
  const res = await response.json();
  return res;
};

const fetchAlgoliaDataSource = async (
  options: AlgoliaOptions,
  search: (options: AlgoliaOptions) => Readonly<Promise<any>>
) => {
  return search(options);
};

const SourceLoader: React.FC<
  Omit<Props, "mt" | "mr" | "mb" | "ml" | "renderPlaceholder"> & {
    setSrcLoaded: React.Dispatch<React.SetStateAction<boolean>>;
    srcLoaded: boolean;
  }
> = (props) => {
  const [src, setSrc] = useState([]);
  const [changedLanguage, setChangedLanguage] = useState<boolean>(false);
  const initiated = useRef(false);
  const { search } = useContext(AlgoliaContext);
  const fetchBody = useRef<BodyInit>();
  const { srcConfig, setSrcLoaded, srcLoaded } = props;
  const language = useContext(LanguageContext);

  useEffect(() => {
    setChangedLanguage(true);
  }, [language.currentLanguage]);

  useEffect(() => {
    //Reloads source if fetch body changes - e.g when toggling favourites
    if (fetchBody.current && fetchBody.current !== srcConfig?.fetch?.body) {
      initiated.current = false;
    }
  }, [fetchBody.current, srcConfig?.fetch, initiated.current]);
  useEffect(() => {
    if (!initiated.current || changedLanguage) {
      setSrcLoaded(false);
      fetchBody.current = srcConfig?.fetch?.body;
      initiated.current = true;
      (async () => {
        const result = srcConfig.fetch
          ? await fetchServerDataSource(srcConfig.fetch)
          : (await fetchAlgoliaDataSource(srcConfig.algolia, search)).hits;

        if (Object.keys(result).length > 0) {
          setSrc(result);
        }
        setSrcLoaded(true);
        setChangedLanguage(false);
      })();
    }
    forceCheck();
  }, [srcConfig, initiated.current, changedLanguage]);

  if (!srcLoaded) {
    return null;
  }

  return <>{props.renderElement(src)}</>;
};
export const FeedItem: React.FC<Props> = (props) => {
  const {
    mt = 0,
    mr = 0,
    mb = 0,
    ml = 0,
    srcConfig,
    renderElement,
    renderPlaceholder,
    lazyLoad = true,
  } = props;
  const classes = useStyles({ mt, mr, mb, ml });
  const [srcLoaded, setSrcLoaded] = useState(false);

  return (
    <div className={classes.root}>
      {!srcConfig ? (
        <>{renderElement()}</>
      ) : (
        <>
          {!srcLoaded && renderPlaceholder()}

          {lazyLoad ? (
            <LazyLoad once offset={500}>
              <SourceLoader
                srcConfig={srcConfig}
                renderElement={renderElement}
                srcLoaded={srcLoaded}
                setSrcLoaded={setSrcLoaded}
              />
            </LazyLoad>
          ) : (
            <SourceLoader
              srcConfig={srcConfig}
              renderElement={renderElement}
              srcLoaded={srcLoaded}
              setSrcLoaded={setSrcLoaded}
            />
          )}
        </>
      )}
    </div>
  );
};
